// const API_BASE = import.meta.env.VITE_API_URL ?? "https://skr.ihma.uz/api";
export const API_BASE = import.meta.env.VITE_API_URL ?? "https://insonuchun.uz/api";

import { getCookie } from "@/lib/cookies";
import { getToken } from "@/lib/auth";
import { toast } from "sonner";

/** API dan keladigan standart xato shablon */
interface ApiErrorBody {
  errors?: Record<string, string[]>;
  title?: string;
  status?: number;
  traceId?: string;
}

/** Barcha API xatolari uchun typed error class */
export class ApiError extends Error {
  readonly status: number;
  readonly traceId?: string;
  /** Xato kelib chiqqan API endpoint (masalan: /MiniApp/ClickLogin) */
  readonly path: string;

  constructor(message: string, status: number, path: string, traceId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.path = path;
    this.traceId = traceId;
  }
}

/**
 * API xato javobidan foydalanuvchiga ko'rsatiladigan xabarni chiqaradi.
 * `errors[""]` ning birinchi elementini oladi, StackTrace ni kesib tashlaydi.
 */
function extractMessage(body: ApiErrorBody): string {
  if (body.errors) {
    for (const messages of Object.values(body.errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        const raw = messages[0];
        // "Xatolik yuz berdi: ..." prefixini va StackTrace dan keyingi texnik qismni olib tashlash
        const clean = raw
          .split(/,?\s*StackTrace:/i)[0]
          .replace(/^Xatolik yuz berdi:\s*/i, "")
          .replace(/^Error:\s*/i, "")
          .trim();
        if (clean) return clean;
      }
    }
  }
  if (body.title) return body.title;
  return "Server xatosi yuz berdi";
}

/** Toast description: endpoint, status, traceId, session cookie holati */
function formatApiErrorDescription(
  path: string,
  status: number,
  traceId: string | undefined
): string {
  const clickSession = getCookie("click-web-session");
  const webSession = getCookie("web-session");
  const sessionHint = clickSession
    ? ` click-wb-sess.n: yes (${clickSession.slice(0, 8)}…)`
    : " click-wb-sess.n: no";
  const webSessionHint = webSession
    ? ` · wb-sess.n: yes (${webSession.slice(0, 8)}…)`
    : " · wb-sess.n: no";

  const base = traceId
    ? `${path} · ${status} · traceId: ${traceId.slice(0, 8)}…`
    : `${path} · Kod: ${status}`;

  return `${base} · ${sessionHint}${webSessionHint}`;
}

/**
 * HTTP xato response ni parse qilib, toast ko'rsatadi va ApiError tashlaydi.
 * `apiGet` / `apiPost` ichidan chaqiriladi.
 */
async function handleApiError(res: Response, path: string): Promise<never> {
  let message = `Xatolik (${res.status})`;
  let traceId: string | undefined;

  try {
    const body = (await res.json()) as ApiErrorBody;
    traceId = body.traceId;
    message = extractMessage(body);
  } catch {
    // JSON parse qilib bo'lmasa status kodi bilan cheklanamiz
  }

  toast.error(message, {
    description: formatApiErrorDescription(path, res.status, traceId),
    duration: 6000,
  });

  throw new ApiError(message, res.status, path, traceId);
}

/**
 * Rasmni to'liq URL ga aylantiradi.
 * imageId — API dan kelgan `image` maydoni (file identifikatori).
 */
/** Yangilik rasmi URL (News DownloadFile) */
export function getPhotoUrl(imageId: string | null | undefined): string | null {
  if (!imageId) return null;
  return `${API_BASE}/News/DownloadFile/${imageId}`;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** Bot/DownloadPhoto — profil va bemor rasmlari uchun */
export function getBotPhotoUrl(photoId: string | null | undefined): string | null {
  if (!photoId || photoId === EMPTY_GUID) return null;
  return `${API_BASE}/MiniApp/DownloadPhoto/${photoId}`;
}

/** localStorage dagi tanlangan til kaliti (i18n bilan sinxron) */
function getLangHeader(): string {
  try {
    return localStorage.getItem("Lang") || "uz_latn";
  } catch {
    return "uz_latn";
  }
}

function getAuthHeader(): string | null {
  const token = getToken();
  return token ? `Bearer ${token}` : null;
}

function baseHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Lang": getLangHeader(),
  };
  const auth = getAuthHeader();
  if (auth) headers["Authorization"] = auth;
  return headers;
}

/**
 * Muvaffaqiyatli (2xx) javob tanasini bardoshli parse qiladi:
 *   - bo'sh tana                      → null
 *   - to'g'ri JSON                    → obyekt/massiv
 *   - JSON bo'lmagan matn (mas. "OK") → xom string
 *
 * `res.json()` oxirgi ikki holatda `SyntaxError` tashlardi va chaqiruvchi buni
 * so'rov muvaffaqiyatsiz bo'lgan deb qabul qilardi. To'lov endpointlari uchun
 * bu eng yomon xato: server pul yechib bo'lgan, UI esa "xato" deb ko'rsatadi.
 * Ba'zi endpointlar (mas. `ZoomradPayFee`) `text/plain` javob qaytaradi.
 */
async function parseOkBody<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return null as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, { method: "GET", headers: baseHeaders() });
  if (!res.ok) await handleApiError(res, path);
  return parseOkBody<T>(res);
}

export async function apiPost<T>(path: string, data?: unknown): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: baseHeaders(),
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });
  if (!res.ok) await handleApiError(res, path);
  return parseOkBody<T>(res);
}

/**
 * Page unload paytida (pagehide eventi) logout kabi so'rovlar uchun.
 * `keepalive: true` — brauzer sahifa yopilsa ham so'rovni tugatishga harakat qiladi.
 * Fire-and-forget: Promise qaytarmaydi, xatolarni jimgina yutadi.
 */
export function apiPostBeacon(path: string): void {
  const url = `${API_BASE}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Lang: getLangHeader(),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  fetch(url, { method: "POST", headers, keepalive: true }).catch(() => {});
}
